import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Bot, Plus, Trash2, Save, HelpCircle } from 'lucide-react';
import {
  adminGetFaq,
  adminUpdateFaq,
  adminGetChatbotConfig,
  adminUpdateChatbotConfig,
} from '@/lib/api';

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `id_${Date.now()}_${Math.random()}`);

const DEFAULT_CONFIG = {
  welcome_message: "Hey! 👋 I'm the NAKI TUNE assistant. How can I help you today?",
  suggested_questions: ['What are your hours?', 'How much does it cost?', 'How do I book?', 'Where are you located?'],
  fallback_message:
    "I'm not sure about that. For specific questions, please contact William at 0278150446 or email Nakitune@outlook.co.nz.",
  proactive_popup_enabled: true,
  proactive_popup_delay_seconds: 5,
  proactive_popup_message: "Got a question? I'm here to help! 💬",
};

export default function ChatbotManager() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingFaq, setSavingFaq] = useState(false);
  const [newQuickQuestion, setNewQuickQuestion] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, faqRes] = await Promise.all([adminGetChatbotConfig(), adminGetFaq()]);
      if (cfgRes.data) setConfig({ ...DEFAULT_CONFIG, ...cfgRes.data });
      const items = Array.isArray(faqRes.data) ? faqRes.data : [];
      // Normalize keywords: backend stores as List[str]; ensure UI always has an array.
      setFaq(items.map((it) => ({ ...it, id: it.id || uuid(), keywords: it.keywords || [] })));
    } catch (_e) {
      toast.error('Failed to load chatbot settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await adminUpdateChatbotConfig(config);
      toast.success('Chatbot settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingConfig(false);
    }
  };

  const saveFaq = async () => {
    // Validate: question + answer non-empty
    const invalid = faq.find((f) => !f.question?.trim() || !f.answer?.trim());
    if (invalid) {
      toast.error('Every FAQ needs a question and an answer');
      return;
    }
    setSavingFaq(true);
    try {
      const payload = faq.map((f) => ({
        id: f.id,
        question: f.question.trim(),
        answer: f.answer.trim(),
        keywords: (f.keywords || []).map((k) => String(k).trim()).filter(Boolean),
      }));
      await adminUpdateFaq(payload);
      toast.success(`FAQ saved — ${payload.length} question${payload.length === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save FAQ');
    } finally {
      setSavingFaq(false);
    }
  };

  const addFaq = () => {
    setFaq((prev) => [
      ...prev,
      { id: uuid(), question: '', answer: '', keywords: [] },
    ]);
  };

  const updateFaq = (id, field, value) => {
    setFaq((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };

  const removeFaq = (id) => {
    setFaq((prev) => prev.filter((f) => f.id !== id));
  };

  const addQuickQuestion = () => {
    const q = newQuickQuestion.trim();
    if (!q) return;
    setConfig((prev) => ({
      ...prev,
      suggested_questions: [...(prev.suggested_questions || []), q],
    }));
    setNewQuickQuestion('');
  };

  const removeQuickQuestion = (idx) => {
    setConfig((prev) => ({
      ...prev,
      suggested_questions: (prev.suggested_questions || []).filter((_, i) => i !== idx),
    }));
  };

  if (loading) {
    return (
      <div className="glass p-12 border border-zinc-800 bg-zinc-900/50 text-center">
        <Bot className="w-10 h-10 mx-auto mb-3 text-zinc-600 animate-pulse" />
        <p className="text-zinc-500">Loading chatbot settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chatbot-manager">
      {/* ===== Popup / Welcome Config ===== */}
      <section className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4" data-testid="chatbot-config-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white flex items-center gap-2">
            <Bot className="w-6 h-6 text-green-500" /> Chatbot Settings
          </h2>
          <Button onClick={saveConfig} disabled={savingConfig} className="bg-green-600 hover:bg-green-700" data-testid="save-chatbot-config-btn">
            <Save className="w-4 h-4 mr-2" />
            {savingConfig ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-zinc-300">Welcome message (first thing visitors see)</Label>
            <Textarea
              value={config.welcome_message}
              onChange={(e) => setConfig((prev) => ({ ...prev, welcome_message: e.target.value }))}
              className="bg-zinc-950 border-zinc-800 text-zinc-200"
              rows={2}
              data-testid="chatbot-welcome-input"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="text-zinc-300">Fallback message (when the bot can't match a question)</Label>
            <Textarea
              value={config.fallback_message}
              onChange={(e) => setConfig((prev) => ({ ...prev, fallback_message: e.target.value }))}
              className="bg-zinc-950 border-zinc-800 text-zinc-200"
              rows={3}
              data-testid="chatbot-fallback-input"
            />
          </div>

          <div className="space-y-3 md:col-span-2 pt-2 border-t border-zinc-800">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-zinc-300">Auto-popup bubble on homepage</Label>
                <p className="text-xs text-zinc-500">Shows a friendly nudge after a few seconds so visitors notice the bot.</p>
              </div>
              <Switch
                checked={!!config.proactive_popup_enabled}
                onCheckedChange={(v) => setConfig((prev) => ({ ...prev, proactive_popup_enabled: !!v }))}
                data-testid="chatbot-popup-toggle"
              />
            </div>

            {config.proactive_popup_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-zinc-300">Popup message</Label>
                  <Input
                    value={config.proactive_popup_message}
                    onChange={(e) => setConfig((prev) => ({ ...prev, proactive_popup_message: e.target.value }))}
                    className="bg-zinc-950 border-zinc-800 text-zinc-200"
                    data-testid="chatbot-popup-message-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Delay (seconds)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={60}
                    value={config.proactive_popup_delay_seconds}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        proactive_popup_delay_seconds: Math.max(0, Math.min(60, parseInt(e.target.value, 10) || 0)),
                      }))
                    }
                    className="bg-zinc-950 border-zinc-800 text-zinc-200"
                    data-testid="chatbot-popup-delay-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Quick questions */}
          <div className="space-y-2 md:col-span-2 pt-2 border-t border-zinc-800">
            <Label className="text-zinc-300">Quick-tap questions (shown as buttons under the welcome)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {(config.suggested_questions || []).map((q, i) => (
                <span
                  key={`${q}-${i}`}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-zinc-800 text-zinc-200 border border-zinc-700 rounded-full text-sm"
                  data-testid={`chatbot-quick-chip-${i}`}
                >
                  {q}
                  <button
                    onClick={() => removeQuickQuestion(i)}
                    className="text-zinc-500 hover:text-red-400"
                    aria-label="Remove"
                    data-testid={`chatbot-quick-remove-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Input
                value={newQuickQuestion}
                onChange={(e) => setNewQuickQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addQuickQuestion(); } }}
                placeholder="e.g. Do you tune turbo cars?"
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
                data-testid="chatbot-quick-new-input"
              />
              <Button onClick={addQuickQuestion} variant="outline" className="border-zinc-700" data-testid="chatbot-quick-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ List ===== */}
      <section className="glass p-6 border border-zinc-800 bg-zinc-900/50 space-y-4" data-testid="chatbot-faq-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-green-500" /> FAQ Answers
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              The bot matches a visitor's question against each FAQ's <span className="text-zinc-300">keywords</span> and replies with the matching answer.
              Add common misspellings and synonyms to catch more questions.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={addFaq} variant="outline" className="border-green-700 text-green-500 hover:bg-green-900/30" data-testid="chatbot-add-faq-btn">
              <Plus className="w-4 h-4 mr-1" /> New Question
            </Button>
            <Button onClick={saveFaq} disabled={savingFaq} className="bg-green-600 hover:bg-green-700" data-testid="chatbot-save-faq-btn">
              <Save className="w-4 h-4 mr-2" />
              {savingFaq ? 'Saving...' : 'Save All FAQ'}
            </Button>
          </div>
        </div>

        {faq.length === 0 && (
          <div className="p-10 border border-dashed border-zinc-800 rounded-md text-center">
            <HelpCircle className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
            <p className="text-zinc-500 text-sm">No FAQ yet — click <span className="text-green-500">New Question</span> to add your first one.</p>
          </div>
        )}

        <div className="space-y-4">
          {faq.map((item, idx) => (
            <div key={item.id} className="p-4 border border-zinc-800 rounded-md bg-zinc-950/60 space-y-3" data-testid={`faq-item-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-zinc-500">Question #{idx + 1}</span>
                <button
                  onClick={() => removeFaq(item.id)}
                  className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                  data-testid={`faq-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">Question (shown to admin)</Label>
                  <Input
                    value={item.question}
                    onChange={(e) => updateFaq(item.id, 'question', e.target.value)}
                    placeholder="e.g. How much does a dyno session cost?"
                    className="bg-zinc-900 border-zinc-800 text-zinc-200"
                    data-testid={`faq-question-${idx}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-xs">Keywords (comma-separated — what triggers this answer)</Label>
                  <Input
                    value={(item.keywords || []).join(', ')}
                    onChange={(e) =>
                      updateFaq(
                        item.id,
                        'keywords',
                        e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      )
                    }
                    placeholder="price, cost, how much, rate"
                    className="bg-zinc-900 border-zinc-800 text-zinc-200"
                    data-testid={`faq-keywords-${idx}`}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Answer</Label>
                <Textarea
                  value={item.answer}
                  onChange={(e) => updateFaq(item.id, 'answer', e.target.value)}
                  placeholder="Type the response the bot should give..."
                  className="bg-zinc-900 border-zinc-800 text-zinc-200"
                  rows={3}
                  data-testid={`faq-answer-${idx}`}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
